import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProgressBar } from 'react-step-progress-bar';

import { Arrow } from '@webapp/common/assets/icons';
import { CssTransitionItems } from '@webapp/common/components/css-transition-items/css-transition-items';
import { useTextStyle } from '@webapp/common/components/custom-styles';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { calcPercent, createTextStyle } from '@webapp/common/lib/ui';
import { CANTRATE_ID, getKanoAnswers } from '@webapp/common/resources/constants';
import type { InfoModel } from '@webapp/common/resources/entities/info';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';

import { KanoRow } from './kano-row';

import css from './kano.css';

export const Kano: VFC<SurveyQuestion> = ({ question, actor }) => {
    const {
        answersActors,
        id: questionId,
        groupsActors,
        params: { comment, commentText: commentTextHelp, cantRate, commentRequired, cantRateText, kanoAnswers },
    } = question;

    const {
        info: {
            design: {
                fonts: {
                    questionTextBold,
                    questionTextColor,
                    questionTextItalic,
                    questionTextSize,
                    questionTextThrough,
                    questionTextUnderline,
                    elementColor,
                },
            },
            params: {
                alert: { kanoRow: kanoRowLabel },
            },
        },
    } = (actor.getSnapshot().context.parentRef.getSnapshot() as any).context as { info: InfoModel };
    const { listStyle, textStyle } = useTextStyle();
    const headerTitleStyles: CSSProperties = createTextStyle({
        underline: questionTextUnderline,
        through: questionTextThrough,
        bold: questionTextBold,
        italic: questionTextItalic,
        color: questionTextColor,
        size: questionTextSize,
    });

    const [currentRowIndex, setCurrentRowIndex] = useState(0);
    const [answeredCount, setAnsweredCount] = useState(0);

    const answerTexts = useMemo(() => getKanoAnswers(kanoAnswers), [kanoAnswers]);

    // Filter out hidden answers
    const visibleAnswersActors = answersActors.filter((answerActor) => {
        const answer = answerActor.getSnapshot().context;
        return !answer.hidden && !answer.hiddenByAnswersHide;
    });

    // Отвеченной считается строка, где выбраны оба варианта (функция есть / нет)
    // либо отмечено «Не могу оценить». Индикатор показывает количество отвеченных,
    // а не индекс текущей строки (webapp#1199).
    const getAnsweredCount = useCallback(
        () =>
            answersActors.reduce((acc, answerActor) => {
                const answer = answerActor.getSnapshot().context;
                if (answer.hidden || answer.hiddenByAnswersHide) return acc;
                const response = answer.response?.getSnapshot().context;
                const isAnswered =
                    response &&
                    (response.value === CANTRATE_ID || (Boolean(response.value) && Boolean(response.extra)));
                return isAnswered ? acc + 1 : acc;
            }, 0),
        [answersActors],
    );

    useEffect(() => {
        const updateAnsweredCount = () => setAnsweredCount(getAnsweredCount());
        updateAnsweredCount();
        const subscriptions = answersActors
            .map((answerActor) => answerActor.getSnapshot().context.response?.subscribe(updateAnsweredCount))
            .filter(Boolean);
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
    }, [answersActors, getAnsweredCount]);

    useEffect(() => {
        // Reset to first row when question is shown
        setCurrentRowIndex(0);
    }, [questionId]);

    const handlePrev = useCallback(() => {
        setCurrentRowIndex((prev) => Math.max(0, prev - 1));
    }, []);

    const handleNext = useCallback(() => {
        setCurrentRowIndex((prev) => Math.min(visibleAnswersActors.length - 1, prev + 1));
    }, [visibleAnswersActors.length]);

    const getPercent = useCallback(
        () => calcPercent(answeredCount, visibleAnswersActors.length),
        [answeredCount, visibleAnswersActors.length],
    );

    const currentAnswerActor = visibleAnswersActors[currentRowIndex];

    const showNavigation = visibleAnswersActors.length > 1;

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.KANO, css.root)} style={listStyle}>
            {showNavigation && (
                <div className={css.progressBarWrap}>
                    <span>{kanoRowLabel}</span>
                    <div className={css.progressBarContainer}>
                        <span>
                            {answeredCount} / {visibleAnswersActors.length}
                        </span>
                        <div className={css.progressBar}>
                            <ProgressBar
                                percent={getPercent()}
                                unfilledBackground='#ccc'
                                filledBackground={elementColor}
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className={css.wrap}>
                <CssTransitionItems type='fade' items={visibleAnswersActors} activeItem={currentRowIndex}>
                    {currentAnswerActor && (
                        <KanoRow
                            questionId={questionId}
                            labelStyle={headerTitleStyles}
                            answer={currentAnswerActor}
                            answerTexts={answerTexts}
                            groupsActors={groupsActors}
                            style={textStyle}
                            comment={comment}
                            commentTextHelp={commentTextHelp}
                            showCantRate={cantRate}
                            commentRequired={commentRequired}
                            cantRateText={cantRateText}
                            onNext={handleNext}
                        />
                    )}
                </CssTransitionItems>
            </div>
            {showNavigation && (
                <div className={css.arrows}>
                    <Arrow
                        className={cn(css.leftArrow, currentRowIndex === 0 && css.leftArrowDisabled)}
                        onClick={handlePrev}
                        color={elementColor}
                    />
                    <Arrow
                        className={cn(
                            css.rightArrow,
                            currentRowIndex === visibleAnswersActors.length - 1 && css.rightArrowDisabled,
                        )}
                        onClick={handleNext}
                        color={elementColor}
                    />
                </div>
            )}
        </div>
    );
};
